/* eslint-disable no-irregular-whitespace */
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { debounce } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import TextField from 'components/TextField';

import ROUTERS_PATHS from 'consts/router-paths';
import Icons from 'consts/svgIcons';
import { useNavigate } from 'react-router';
import { useGetInputValue, useSetInputValue } from 'redux/searchText/searchText';
import { useCountSearch, useGetSearchBar } from 'resources/userSide/home';
import { checkTypeUrl, formatContent, formatContentFromHtmlString, formatText } from 'utils';
import styles from './styles.module.scss';

const titleContent = `<p class="MsoNormal">Lesson 5: IT Terminology </p><p class="MsoListParagraphCxSpFirst" style="text-indent:-.25in;mso-list:l1 level1 lfo1">
<!-- [if !supportLists] -->·&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
<!-- [endif] -->Common IT-related vocabulary and phrases </p><p class="MsoListParagraphCxSpLast" style="text-indent:-.25in;mso-list:l1 level1 lfo1">
<!-- [if !supportLists] -->·&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
<!-- [endif] -->Describing job roles and responsibilities inEnglish </p><p class="MsoNormal">Lesson 6: Email Writing Skills </p><p class="MsoListParagraphCxSpFirst" style="text-indent:-.25in;mso-list:l0 level1 lfo2">
<!-- [if !supportLists] -->·&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
<!-- [endif] -->Writing concise and effective emails </p><p class="MsoListParagraphCxSpLast" style="text-indent:-.25in;mso-list:l0 level1 lfo2">
<!-- [if !supportLists] -->·&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
<!-- [endif] -->Formal and informal communication in an ITcontext</p><p>
<!-- [if gte mso 9]>      <![endif] -->​​​
<!-- [if gte mso 9]>                                                                                                  <![endif] -->​
<!-- [if gte mso 9]>                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  <![endif] -->​
<!-- [endif] -- -->​
<!-- StartFragment -->​<br></p>`;

const cx = classNames.bind(styles);
interface ISearchComponentProps {
  pageType: string;
  handleClear?: () => void;
}

const SearchComponent = (props: ISearchComponentProps) => {
  const navigate = useNavigate();
  const { pageType, handleClear } = props;

  const { onChangeInputValue } = useSetInputValue();
  const previewTopic = localStorage.getItem('previewInfo');

  const [isClickOutSide, setClickOutSide] = useState<boolean>(false);
  const [titleSearch, setTitleSearch] = useState<string>('');
  const inputRef = useRef(null);
  const { inputValue } = useGetInputValue();

  useClickAway(inputRef, () => {
    setClickOutSide(true);
    handleClear && handleClear();
  });

  const [isClickSearch, setIsClickSearch] = useState<boolean>(false);
  const { isDisplayHeaderSearch } = useGetInputValue();

  const debounceFn = useCallback(
    debounce((value: string) => handleDebounceFn(value), 300),
    []
  );

  const handleDebounceFn = (value: string) => {
    setTitleSearch(value);
  };

  const handleClearInputValue = () => {
    onChangeInputValue('');
  };
  const { list, isFetching: loading } = useGetSearchBar(titleSearch.trim());
  const { countSearch, isLoading: isLoadingCount } = useCountSearch();

  const isShowSearchResult = useMemo(() => {
    if (inputValue) {
      if (list && list.length > 0) {
        if (!isClickOutSide) {
          return true;
        }
        return false;
      }
    }
    return false;
  }, [inputValue, JSON.stringify(list), isClickOutSide]);

  const generateVariations = (text: string) => {
    const variations: any = [];
    // Tạo một mảng ban đầu với từ gốc (viết thường)
    variations.push(text.toLowerCase());
    // Duyệt qua từng ký tự của từ
    text.split('').forEach((char, index) => {
      // Tạo một bản sao của mảng biến thể hiện tại
      const currentVariations = [...variations];
      // Duyệt qua các biến thể hiện tại
      currentVariations.forEach((variant, variantIndex) => {
        // Tạo biến thể với ký tự viết hoa
        const variantWithUppercase = variant.substring(0, index) + char.toUpperCase() + variant.substring(index + 1);
        variations.push(variantWithUppercase);
      });
    });
    return variations;
  };

  const capitalizeFirstLetter = (string: any) => {
    return string.replace(/\b\w/g, (match: any) => {
      return match.toUpperCase();
    });
  };

  const handleMarkWords = (text: string) => {
    if (!text) return '';
    const words: any = [];
    const textSlice = text.slice(0, 100);
    const sentences = textSlice.split(/[.!?]/);

    sentences.forEach((sentence) => {
      const sentenceWords = sentence.split(/\s+/);
      const sentenceTitleSearchs = titleSearch.trim().split(/\s+/);
      sentenceWords.forEach((sentenceWord) => {
        sentenceTitleSearchs.forEach((sentenceTitleSearch) => {
          const isCheck = sentenceWord.toLowerCase().includes(sentenceTitleSearch.toLowerCase());
          if (isCheck) {
            const wordCapital = capitalizeFirstLetter(sentenceTitleSearch);
            words.push(sentenceTitleSearch, wordCapital);
          }
        });
      });
    });
    const clearDuplicate = Array.from(new Set(words));
    let stringText = text;

    clearDuplicate
      .filter((y) => !!y)
      .forEach((x: any) => {
        const words = stringText.split(' ');
        stringText = words
          .map((word) => {
            if (!(word.includes('markWordsText') || word.includes('<span') || word.includes('</span>'))) {
              return word.replaceAll(x, `<span className="markWordsText">${x}</span>`);
            }
            return word;
          })
          .join(' ');
      });
    return stringText as string;
  };

  const onClickSearch = useCallback(
    (id: string, slugUrl: string, categorySlugUrl?: string) => {
      countSearch({
        keyword: titleSearch.trim().replace(/\s+/g, ' '),
        articleId: id
      })
        .unwrap()
        .then((res: any) => {
          if (res?.data?.errorMessage) {
            console.log(res.data.errorMessage);
          } else {
            console.log('Count Search with articleId successfully!');
          }
        })
        .finally(() => {
          setTimeout(() => {
            setIsClickSearch(false);
          }, 1000);
          setTitleSearch('');
          // navigate(`${ROUTERS_PATHS.TOPIC_DETAIL}/${slugUrl}`);
          checkTypeUrl()
          navigate(
            ROUTERS_PATHS.TOPIC_DETAIL.replace(':categoryUrl', categorySlugUrl || '').replace(':articleUrl', slugUrl)
          );
          pageType !== 'home' && window.location.reload();
        });
    },
    [titleSearch]
  );

  const onBlurSearch = useCallback(() => {
    if (titleSearch.trim() && titleSearch.trim() !== '') {
      countSearch({ keyword: titleSearch.trim().replace(/\s+/g, ' ') })
        .unwrap()
        .then((res: any) => {
          if (res?.data?.errorMessage) {
            console.log(res.data.errorMessage);
          } else {
            console.log('Count Search successfully!');
          }
        })
        .finally(() => {
          setIsClickSearch(false);
        });
    }
  }, [titleSearch]);

  return (
    <Box 
      width={'100%'} 
      position={'relative'} 
      className={styles.container} 
      ref={inputRef}
      sx={{
          '@media print': {
            display: 'none !important',
          }
        }}
    >
      <TextField
        placeholder="Search for a topic..."
        disabled={!!previewTopic}
        className={cx('text-field')}
        onChange={(e) => {
          debounceFn(e.currentTarget.value);
          onChangeInputValue(e.currentTarget.value);
          setClickOutSide(false);
        }}
        sx={{ maxWidth: 576 }}
        value={inputValue}
        InputProps={{
          startAdornment: <Icons.SearchIcon sx={{ marginRight: '5px', color: '#8B8B8B' }} />,
          endAdornment: inputValue && <ClearIcon sx={{ cursor: 'pointer' }} onClick={handleClearInputValue} />
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!isClickSearch) {
              onBlurSearch();
            }
          }, 500);
        }}
      />

      <div
        className={cx('search-result-container', `search-result-container-${pageType}`)}
        id="search-result-container-header"
        style={
          {
            // maxWidth: '576px',
            // marginTop: isDisplayHeaderSearch ? "8px" : undefined,
          }
        }
      >
        {isShowSearchResult ? (
          <div className={cx('search-result-wrapper')}>
            <Typography style={{ display: 'flex' }} className={cx('search-result-label')}>
              {`Search results for "${inputValue}"`}
              {loading && <Icons.BarsScale style={{ width: '30px', height: '17px', paddingTop: '3px' }} />}
            </Typography>
            <Box className={cx('topic-list')}>
              {loading ? (
                ''
              ) : (
                <ul>
                  {list?.map((item: any) => {
                    const stringContent = formatContentFromHtmlString(item.content) as string;
                    const stringTitle = formatText(item.title) as string;
                    const title = handleMarkWords(stringTitle);
                    const content = handleMarkWords(stringContent);

                    return (
                      <li key={item.id}>
                        <Box
                          onMouseDown={() => {
                            setIsClickSearch(true);
                            onClickSearch(item.id, item.slugUrl, item?.categorySlugUrl);
                          }}
                        >
                          <Typography
                            className={cx('heading-title', 'text-one-line')}
                            dangerouslySetInnerHTML={{ __html: title }}
                          />
                          <Typography
                            className={cx('body-content', 'text-one-line')}
                            dangerouslySetInnerHTML={{ __html: content }}
                          />
                        </Box>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Box>
          </div>
        ) : inputValue && !isClickOutSide && list.length === 0 ? (
          <Box className={cx('search-result-wrapper')}>
            <Typography
              className={cx('search-result-label')}
              sx={{ marginBottom: '0px !important', wordBreak: 'break-word' }}
            >
              {`Sorry, we found 0 results for "${inputValue}"`}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </div>
    </Box>
  );
};

export default SearchComponent;
